import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import ReactInputMask from "react-input-mask";
import { z } from "zod";

import { validatePhoneNumber } from "../shared/utils";

// TODO: Wait for actual URLs
export const TERMS_URL = "https://www.findkeep.love/pages/terms";
export const PRIVACY_URL = "https://www.findkeep.love/pages/privacy-policy";
export const EXCLUSIONS_URL = "https://www.findkeep.love/pages/exclusions";
const LOGO_URL =
  "https://cdn.shopify.com/s/files/1/0265/0062/8528/files/FKL-Logo_Rainbow.png?v=1659634195";

const formSchema = z.object({
  phoneNumber: z
    .string()
    .min(1, { message: "Phone number is required" })
    .refine(validatePhoneNumber, {
      message: "Please enter a valid phone number",
    }),
  solicitationAccepted: z.literal<boolean>(true, {
    errorMap: () => ({
      message: "You must agree to receive solicitation SMS messages",
    }),
  }),
  promotionalAccepted: z.literal<boolean>(true, {
    errorMap: () => ({
      message: "You must agree to receive promotional SMS messages",
    }),
  }),
  transactionalAccepted: z.literal<boolean>(true, {
    errorMap: () => ({
      message: "You must agree to receive transactional SMS messages",
    }),
  }),
});

type SmsOptinFormData = z.infer<typeof formSchema>;

export function Widget() {
  const [isSuccess, setIsSuccess] = useState<boolean>(false);

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<SmsOptinFormData>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: {
      phoneNumber: "",
      solicitationAccepted: false,
      promotionalAccepted: false,
      transactionalAccepted: false,
    },
  });

  const onSubmit = () => {
    // Display-only mode for carrier approval - no actual submission
    setIsSuccess(true);
  };

  if (isSuccess) {
    return (
      <div className="sms-optin">
        <div className="sms-optin__header">
          <img className="sms-optin__logo" src={LOGO_URL} alt="FindKeep.Love" />
        </div>
        <div className="sms-optin__success">
          <p className="sms-optin__success-title">Thank you!</p>
          <p className="sms-optin__success-message">
            You have successfully signed up for text messages.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="sms-optin">
      <div className="sms-optin__header">
        <img className="sms-optin__logo" src={LOGO_URL} alt="FindKeep.Love" />
      </div>

      <p className="sms-optin__title">
        Sign up for text messages to receive updates and announcements
      </p>

      <form className="sms-optin__form" onSubmit={handleSubmit(onSubmit)}>
        <label className="sms-optin__consent">
          <input type="checkbox" {...register("solicitationAccepted")} />
          <span className="sms-optin__consent-text">
            By submitting this form and providing your cell phone number, you
            agree to receive recurring automated solicitation text messages
            (e.g. offers and marketing outreach) from FindKeep.Love at the cell
            number used when signing up. Consent is not a condition of any
            purchase. Text HELP for help and STOP to cancel text messages. Msg
            frequency varies. *View{" "}
            <a
              href={TERMS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="sms-optin__link"
            >
              Terms
            </a>
            ,{" "}
            <a
              href={PRIVACY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="sms-optin__link"
            >
              Privacy
            </a>{" "}
            &{" "}
            <a
              href={EXCLUSIONS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="sms-optin__link"
            >
              Exclusions
            </a>
          </span>
        </label>
        {errors.solicitationAccepted && (
          <div className="sms-optin__error">
            {errors.solicitationAccepted.message}
          </div>
        )}

        <label className="sms-optin__consent">
          <input type="checkbox" {...register("promotionalAccepted")} />
          <span className="sms-optin__consent-text">
            By submitting this form and providing your cell phone number, you
            agree to receive recurring automated promotion text messages (e.g.
            giveaway promotion) from FindKeep.Love at the cell number used when
            signing up. Consent is not a condition of any purchase. Text HELP
            for help and STOP to cancel text messages. Msg frequency varies.
            *View{" "}
            <a
              href={TERMS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="sms-optin__link"
            >
              Terms
            </a>
            ,{" "}
            <a
              href={PRIVACY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="sms-optin__link"
            >
              Privacy
            </a>{" "}
            &{" "}
            <a
              href={EXCLUSIONS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="sms-optin__link"
            >
              Exclusions
            </a>
          </span>
        </label>
        {errors.promotionalAccepted && (
          <div className="sms-optin__error">
            {errors.promotionalAccepted.message}
          </div>
        )}

        <label className="sms-optin__consent">
          <input type="checkbox" {...register("transactionalAccepted")} />
          <span className="sms-optin__consent-text">
            By submitting this form and providing your cell phone number, you
            agree to receive recurring automated transactional text messages
            (e.g. giveaway reminders) from FindKeep.Love at the cell number used
            when signing up. Consent is not a condition of any purchase. Text
            HELP for help and STOP to cancel text messages. Msg frequency
            varies. *View{" "}
            <a
              href={TERMS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="sms-optin__link"
            >
              Terms
            </a>
            ,{" "}
            <a
              href={PRIVACY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="sms-optin__link"
            >
              Privacy
            </a>{" "}
            &{" "}
            <a
              href={EXCLUSIONS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="sms-optin__link"
            >
              Exclusions
            </a>
          </span>
        </label>
        {errors.transactionalAccepted && (
          <div className="sms-optin__error">
            {errors.transactionalAccepted.message}
          </div>
        )}

        <div className="sms-optin__input-container">
          <Controller
            name="phoneNumber"
            control={control}
            render={({ field }) => (
              <ReactInputMask
                className="sms-optin__input"
                mask="(999) 999-9999"
                placeholder="Phone Number"
                aria-label="Phone Number"
                {...field}
              />
            )}
          />
          {errors.phoneNumber && (
            <div className="sms-optin__error">{errors.phoneNumber.message}</div>
          )}
        </div>

        <div className="sms-optin__submit-container">
          <div className="sms-optin__button-wrapper">
            <button
              type="submit"
              className="sms-optin__submit-button"
              disabled={!isValid}
            >
              Submit
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
